import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, createBlock as _createBlock, TransitionGroup as _TransitionGroup } from "vue"

const _hoisted_1 = { class: "uploader" }
const _hoisted_2 = ["accept", "multiple"]
const _hoisted_3 = {
  key: 0,
  class: "curtain fit"
}
const _hoisted_4 = {
  key: 0,
  class: "uploading-options-dialog"
}
const _hoisted_5 = { class: "column" }
const _hoisted_6 = { class: "row justify-between items-center no-wrap full-width" }
const _hoisted_7 = { class: "file-name text-h6 base-margin-right" }
const _hoisted_8 = {
  key: 0,
  class: "row justify-between"
}

import { AxiosResponse } from 'axios';
import { cloneDeep } from 'lodash';

/* eslint-disable @typescript-eslint/naming-convention,no-underscore-dangle */
import { ref } from 'vue';

import NestedFolderLinkOption from 'SharedComponents/Attachments/NestedFolderLinkOption.vue';
import SBtn from 'SharedComponents/ui/Button/SBtn.vue';
// eslint-disable-next-line import/order
import { FileUploadOptions } from 'SharedDir/declarations/files';


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'Uploader' },
  __name: 'Uploader',
  props: {
    maxSize: {},
    maxFiles: { default: 10 },
    accept: {},
    baseUploadingOptions: { default: () => ({}) },
    uploader: {},
    batchUploader: {},
    askBeforeUpload: { type: Boolean, default: false }
  },
  emits: ["start", "finish"],
  setup(__props: any, { emit: __emit }) {


const props = __props;
const emits = __emit;
const refFileInput = ref<HTMLInputElement>(null as any);
const errors = ref<string[]>([]);
const innerErrors = ref<string[]>([]);
const files = ref<any>([]);
const uploading = ref(false);
const uploadOptionsDialog = ref(false);
/**
 * Обещание, разрешения которого мы будем ожидать для загрузки файлов с опциями.
 * Которые пользователь мог отредактировать с включенным флагом `askBeforeUpload
 */
let optionEditingPromise: Promise<void>;
const confirmUploading = ref<() => void>(() => {});
const abortUploading = ref<() => void>(() => {});
/**
 * Массив файлов и опций загрузки на сервер, которые при наличии флага `askBeforeUpload`
 * пользователь может отредактировать. Пока реализовано только редактирование опции
 * создания "ярлыков" загружаемого файла во вложенных директориях. И то только на 1 уровень вниз.
 *
 * @type {Ref<UnwrapRef<{file: File, options: FileUploadOptions}[]>>}
 */
const filesWithOptions = ref([] as { file: File; options: FileUploadOptions }[]);

async function uploadWithOptionConfirmation(fit: Array<File>) {
  filesWithOptions.value = fit.map((file) => ({
    file,
    options: cloneDeep(props.baseUploadingOptions),
  }));
  /**
   * Дожидаемся когда пользователь отредактирует опции для загрузки всех файлов,
   * ну или нажмет отмену
   */
  await optionEditingPromise;
  startUploading(filesWithOptions.value.map(({ file }) => file));
  const requestsData = filesWithOptions.value.map(
    ({ file, options }) => [createFileList(file), options] as [FileList, FileUploadOptions]
  );
  return sendRequests(makeUploadingRequests(requestsData));
}

async function upload(event: Event) {
  reset();
  const { files: files_ } = event.target as HTMLInputElement;
  if (!files_) {
    return;
  }
  if (files_.length > props.maxFiles) {
    errors.value = [
      `Превышен лимит на количество файлов. Максимальное возможное количество - ${props.maxFiles}.`,
    ];
    finishUploading();
    return;
  }

  const { fit, tooBig } = processFileSizes(files_);

  if (tooBig.length !== 0 && props.maxSize) {
    const maxSize = Intl.NumberFormat('ru').format(props.maxSize);
    errors.value = tooBig.map(
      (file) => `"${file.name}" превышает максимально допустимый размер - ${maxSize} Кб`
    );
    if (fit.length === 0) {
      finishUploading();
      return;
    }
  }

  let success;
  let fail;

  if (props.askBeforeUpload) {
    try {
      showFileOptionsDialog();
      ({ success, fail } = await uploadWithOptionConfirmation(fit));
      closeFileOptionsDialog();
    } catch (e) {
      closeFileOptionsDialog();
      reset();
      return;
    }
  } else {
    startUploading(fit);
    ({ success, fail } = await uploadFiles(fit));
  }

  console.debug(success, fail);
  files.value = success.map(({ data }) => data);
  errors.value = errors.value.concat(fail);

  finishUploading();
}

function processFileSizes(files_: FileList): { fit: File[]; tooBig: File[] } {
  if (!props.maxSize) {
    return { fit: Array.from(files_), tooBig: [] };
  }
  const fit: Array<File> = [];
  const tooBig: Array<File> = [];
  Array.from(files_).forEach((file) => {
    const max = <number>props.maxSize * 1024;
    if (file.size <= max) {
      fit.push(file);
    } else {
      tooBig.push(file);
    }
  });
  return { fit, tooBig };
}

function finishUploading() {
  uploading.value = false;
  emits('finish', { errors: errors.value, files: files.value });
  refFileInput.value.value = '';
}

function startUploading(files_: Array<File>) {
  uploading.value = true;
  emits('start', files_);
}

function reset() {
  errors.value = [];
  files.value = [];
  if (props.askBeforeUpload) {
    optionEditingPromise = new Promise((resolve, reject) => {
      confirmUploading.value = resolve;
      abortUploading.value = reject;
    });
  } else {
    confirmUploading.value = () => {};
    abortUploading.value = () => {};
  }
}

async function sendRequests(requests: Promise<AxiosResponse>[]) {
  const responses = await Promise.allSettled(requests);
  const success: Array<any> = [];
  const fail: Array<any> = [];
  responses.forEach((response) => {
    if (response.status === 'fulfilled') {
      success.push(response.value);
    } else {
      fail.push(response.reason);
    }
  });
  return { success, fail };
}

function makeUploadingRequests(data: [FileList, FileUploadOptions][]) {
  const { uploader } = props;
  if (typeof uploader === 'undefined') {
    throw new Error("You didn't set uploading function!");
  }
  return data.map(([file, options]) => uploader(file, options));
}

async function uploadFiles(list: Array<File>) {
  const options = props.baseUploadingOptions;
  const fileList = list.map(
    (f) => [createFileList(f), props.baseUploadingOptions] as [FileList, FileUploadOptions]
  );
  const requests = makeUploadingRequests(fileList);
  return sendRequests(requests);
}

function createFileList(file: Array<File> | File): FileList {
  const d = new DataTransfer();
  const files_ = Array.isArray(file) ? file : [file];
  files_.forEach((f) => {
    d.items.add(f);
  });
  return d.files;
}

function openNativeUploadDialog() {
  refFileInput.value.click();
}

function showFileOptionsDialog() {
  uploadOptionsDialog.value = true;
  innerErrors.value = errors.value;
  errors.value = [];
}

function closeFileOptionsDialog() {
  innerErrors.value = [];
  uploadOptionsDialog.value = false;
  filesWithOptions.value = [];
}

/**
 * Удаляем конкретный файл из меню опций перед загрузкой
 * @param {number} index
 */
function removeFromConfirmation(index: number) {
  filesWithOptions.value.splice(index, 1);
  if (filesWithOptions.value.length === 0) {
    abortUploading.value();
    closeFileOptionsDialog();
  }
}

function removeInnerError(index: number) {
  innerErrors.value.splice(index, 1);
  if (innerErrors.value.length === 0) {
    abortUploading.value();
    closeFileOptionsDialog();
  }
}

return (_ctx: any,_cache: any) => {
  const _component_s_icon = _resolveComponent("s-icon")!
  const _component_q_banner = _resolveComponent("q-banner")!
  const _component_q_btn = _resolveComponent("q-btn")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("input", {
      ref_key: "refFileInput",
      ref: refFileInput,
      class: "file-input-element",
      accept: _ctx.accept,
      type: "file",
      multiple: _ctx.maxFiles !== 1,
      onChange: upload
    }, null, 40, _hoisted_2),
    _renderSlot(_ctx.$slots, "default", {
      trigger: openNativeUploadDialog,
      uploading: uploading.value
    }, () => [
      _createElementVNode("span", { onClick: openNativeUploadDialog }, "Загрузить")
    ]),
    _createVNode(_Transition, { name: "jump-up" }, {
      default: _withCtx(() => [
        (uploadOptionsDialog.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(_Transition, { name: "fall-down" }, {
      default: _withCtx(() => [
        (uploadOptionsDialog.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _cache[0] || (_cache[0] = _createElementVNode("span", { class: "text-center text-italic base-margin-bottom" }, " Пожалуйста укажите уровень, на который необходимо сохранить файл относительно указанной директории. ", -1)),
                _createVNode(_TransitionGroup, {
                  name: "q-transition--scale",
                  class: "column"
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(innerErrors.value, (error, index) => {
                      return (_openBlock(), _createBlock(_component_q_banner, {
                        key: error,
                        "inline-actions": "",
                        class: "text-white bg-error s-mb-sm"
                      }, {
                        action: _withCtx(() => [
                          _createVNode(_component_s_icon, {
                            color: "white",
                            name: "app:delete ",
                            onClick: ($event: any) => (removeInnerError(index))
                          }, null, 8, ["onClick"])
                        ]),
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(error) + " ", 1)
                        ]),
                        _: 2
                      }, 1024))
                    }), 128))
                  ]),
                  _: 1
                }),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filesWithOptions.value, ({ file }, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: index,
                    class: "row full-width items-center base-margin-bottom no-wrap overflow-auto"
                  }, [
                    _createVNode(NestedFolderLinkOption, {
                      modelValue: filesWithOptions.value[index].options.linksToNestedFolders,
                      "onUpdate:modelValue": ($event: any) => ((filesWithOptions.value[index].options.linksToNestedFolders) = $event),
                      disable: uploading.value,
                      class: "base-margin-right"
                    }, null, 8, ["modelValue", "onUpdate:modelValue", "disable"]),
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("span", _hoisted_7, _toDisplayString(file.name), 1),
                      _createVNode(_component_q_btn, {
                        icon: "app:delete-2",
                        flat: "",
                        disable: uploading.value,
                        color: "brand-red",
                        size: "lg",
                        onClick: ($event: any) => (removeFromConfirmation(index))
                      }, null, 8, ["disable", "onClick"])
                    ])
                  ]))
                }), 128)),
                (filesWithOptions.value.length > 0 || innerErrors.value.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      _createVNode(SBtn, {
                        type: "secondary",
                        loading: uploading.value,
                        label: "Отмена",
                        onClick: abortUploading.value
                      }, null, 8, ["loading", "onClick"]),
                      _createVNode(SBtn, {
                        type: "save",
                        loading: uploading.value,
                        label: "Загрузить",
                        onClick: confirmUploading.value
                      }, null, 8, ["loading", "onClick"])
                    ]))
                  : _createCommentVNode("", true)
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})